import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Loader2, ArrowLeft, CheckCircle2 } from 'lucide-react';
import { motion } from 'framer-motion';
import { API } from '../config/api';

const LOGO = '/favicon-512.png';

// "Expérience privée PURO" — accompagnement haut de gamme, sur demande, validation manuelle.
const OPTIONS = [
  { id: 'solo',      label: '1 cours privé',                  price: '70€',  hint: 'Une séance personnalisée' },
  { id: 'recurring', label: '2 cours privés par semaine',     price: '140€', hint: 'Accompagnement régulier' },
  { id: 'group',     label: 'Groupe ou événement privé',      price: 'Sur demande', hint: 'Pour vous & vos proches' },
];

export default function PrivateClass() {
  const navigate = useNavigate();
  const [option, setOption] = useState('solo');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [message, setMessage] = useState('');
  const [signupSource, setSignupSource] = useState('');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!signupSource) {
      setError("Merci d'indiquer comment vous nous avez connue.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/private-classes`, {
        name,
        email,
        phone: phone || null,
        mode: option,
        message: message || null,
        signup_source: signupSource,
      });
      setDone(true);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Une erreur est survenue. Réessayez dans un instant.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F5EFE5]">
      {/* Top bar */}
      <div className="px-4 sm:px-6 py-5">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center text-[#7A6455] hover:text-[#C99A55] transition-colors text-sm"
          data-testid="private-back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" strokeWidth={1.5} />
          Retour
        </button>
      </div>

      {/* Hero */}
      <section className="px-4 sm:px-6 pt-2 pb-12 sm:pb-16">
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="max-w-2xl mx-auto text-center"
        >
          <img src={LOGO} alt="PURO" className="h-12 w-auto mx-auto mb-6 opacity-90" />
          <p className="text-[10px] uppercase tracking-[0.35em] text-[#C99A55] mb-3 font-semibold">
            Sur invitation
          </p>
          <h1 className="font-heading font-light text-3xl sm:text-5xl text-[#493329] tracking-tight leading-[1.1]">
            Expérience privée<br className="hidden sm:block" /> <em className="text-[#9A744C] not-italic">PURO</em>
          </h1>
          <div className="w-10 h-0.5 bg-[#C99A55] mx-auto my-7" />
          <p className="text-base sm:text-lg text-[#4A453E] leading-relaxed max-w-xl mx-auto font-light">
            Profitez d'un accompagnement privé et personnalisé dans un cadre intimiste et exclusif.
          </p>
          <p className="text-xs text-[#AE9885] italic mt-5 max-w-md mx-auto leading-relaxed">
            Service haut de gamme proposé sur demande, étudié au cas par cas par notre équipe.
          </p>
        </motion.div>
      </section>

      {/* Form section */}
      <section className="px-4 sm:px-6 pb-20">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.15 }}
          className="max-w-xl mx-auto"
        >
          {done ? (
            <Card className="border border-[#E9DDCC] rounded-3xl bg-white shadow-[0_8px_40px_rgba(28,26,23,0.04)]">
              <CardContent className="p-10 text-center" data-testid="private-class-done">
                <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-[#C99A55]/12 flex items-center justify-center">
                  <CheckCircle2 className="w-8 h-8 text-[#C99A55]" strokeWidth={1.4} />
                </div>
                <h2 className="font-heading text-2xl text-[#493329] mb-3 font-medium">Demande reçue</h2>
                <p className="text-sm text-[#7A6455] leading-relaxed mb-7 max-w-sm mx-auto">
                  Votre demande a bien été enregistrée. Notre équipe revient vers vous personnellement
                  pour étudier votre besoin et organiser votre expérience privée.
                </p>
                <Link to="/">
                  <Button className="bg-[#C99A55] hover:bg-[#493329] text-white rounded-full h-11 px-9 transition-all duration-400">
                    Retour à l'accueil
                  </Button>
                </Link>
              </CardContent>
            </Card>
          ) : (
            <Card className="border border-[#E9DDCC] rounded-3xl bg-white shadow-[0_8px_40px_rgba(28,26,23,0.04)]">
              <CardContent className="p-7 sm:p-10">
                <form onSubmit={handleSubmit} className="space-y-7">
                  {/* Option selector */}
                  <div className="space-y-2.5">
                    <Label className="text-[10px] uppercase tracking-[0.25em] text-[#AE9885] font-semibold">
                      Votre demande
                    </Label>
                    <div className="space-y-2.5">
                      {OPTIONS.map((opt) => (
                        <label
                          key={opt.id}
                          className={`flex items-center justify-between p-4 sm:p-5 rounded-2xl border cursor-pointer transition-all duration-300 ${
                            option === opt.id
                              ? 'border-[#C99A55] bg-[#FBF7EE]'
                              : 'border-[#E9DDCC] bg-white hover:border-[#D4BE83]'
                          }`}
                          data-testid={`private-option-${opt.id}`}
                        >
                          <div className="flex items-center gap-3.5">
                            <span
                              className={`w-4 h-4 rounded-full border-2 flex items-center justify-center transition-all ${
                                option === opt.id ? 'border-[#C99A55]' : 'border-[#C9C2B5]'
                              }`}
                            >
                              {option === opt.id && <span className="w-2 h-2 rounded-full bg-[#C99A55]" />}
                            </span>
                            <input
                              type="radio"
                              name="option"
                              value={opt.id}
                              checked={option === opt.id}
                              onChange={() => setOption(opt.id)}
                              className="sr-only"
                            />
                            <div>
                              <p className="font-heading text-[15px] text-[#493329] leading-tight">{opt.label}</p>
                              <p className="text-[11px] text-[#AE9885] mt-0.5">{opt.hint}</p>
                            </div>
                          </div>
                          <p
                            className={`text-sm font-heading ${
                              opt.price === 'Sur demande' ? 'text-[#9A744C] italic' : 'text-[#493329] font-medium'
                            }`}
                            data-testid={`private-option-price-${opt.id}`}
                          >
                            {opt.price}
                          </p>
                        </label>
                      ))}
                    </div>
                    <p className="text-[11px] text-[#9A744C] italic pt-2 leading-relaxed" data-testid="private-group-note">
                      Les cours privés en groupe bénéficient également d'avantages exclusifs de lancement.
                    </p>
                  </div>

                  {/* Coordonnées */}
                  <div className="pt-2 space-y-4">
                    <Label className="text-[10px] uppercase tracking-[0.25em] text-[#AE9885] font-semibold block mb-1">
                      Vos coordonnées
                    </Label>

                    <div className="space-y-1.5">
                      <Label htmlFor="pname" className="text-[#493329] text-sm font-normal">
                        Nom complet
                      </Label>
                      <Input
                        id="pname"
                        required
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        className="h-12 rounded-xl border-[#E9DDCC] bg-[#F5EFE5] focus-visible:ring-[#C99A55]"
                        data-testid="private-name"
                      />
                    </div>

                    <div className="space-y-1.5">
                      <Label htmlFor="pemail" className="text-[#493329] text-sm font-normal">
                        Email
                      </Label>
                      <Input
                        id="pemail"
                        type="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="h-12 rounded-xl border-[#E9DDCC] bg-[#F5EFE5] focus-visible:ring-[#C99A55]"
                        data-testid="private-email"
                      />
                    </div>

                    <div className="space-y-1.5">
                      <Label htmlFor="pphone" className="text-[#493329] text-sm font-normal">
                        Téléphone <span className="text-[#AE9885] text-xs">(facultatif)</span>
                      </Label>
                      <Input
                        id="pphone"
                        type="tel"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        className="h-12 rounded-xl border-[#E9DDCC] bg-[#F5EFE5] focus-visible:ring-[#C99A55]"
                        data-testid="private-phone"
                      />
                    </div>

                    <div className="space-y-1.5">
                      <Label htmlFor="psource" className="text-[#493329] text-sm font-normal">
                        Comment nous avez-vous connue ? <span className="text-[#C99A55]">*</span>
                      </Label>
                      <select
                        id="psource"
                        value={signupSource}
                        onChange={(e) => setSignupSource(e.target.value)}
                        required
                        data-testid="private-source"
                        className="flex h-12 w-full rounded-xl border border-[#E9DDCC] bg-[#F5EFE5] px-3 text-[15px] text-[#493329] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C99A55]"
                      >
                        <option value="">— Sélectionnez —</option>
                        <option value="Instagram">Instagram</option>
                        <option value="Facebook">Facebook</option>
                        <option value="Google">Google</option>
                        <option value="Recommandation">Recommandation</option>
                        <option value="Autre">Autre</option>
                      </select>
                    </div>

                    <div className="space-y-1.5">
                      <Label htmlFor="pmsg" className="text-[#493329] text-sm font-normal">
                        Précisions <span className="text-[#AE9885] text-xs">(facultatif)</span>
                      </Label>
                      <textarea
                        id="pmsg"
                        rows={3}
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        placeholder="Disponibilités, objectifs, nombre de personnes pour un événement…"
                        className="w-full rounded-xl border border-[#E9DDCC] bg-[#F5EFE5] px-3 py-2.5 text-[15px] text-[#493329] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C99A55] resize-none"
                        data-testid="private-message"
                      />
                    </div>
                  </div>

                  {error && (
                    <Alert variant="destructive" className="bg-[#C76542]/8 border-[#C76542]/25 text-[#8B4D3F]">
                      <AlertDescription>{error}</AlertDescription>
                    </Alert>
                  )}

                  <div className="pt-2">
                    <Button
                      type="submit"
                      className="w-full bg-[#493329] hover:bg-[#C99A55] text-white h-12 rounded-full text-[15px] font-medium tracking-wide transition-all duration-400 shadow-[0_4px_20px_rgba(28,26,23,0.12)]"
                      disabled={loading}
                      data-testid="private-submit"
                    >
                      {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Envoyer ma demande'}
                    </Button>
                    <p className="text-center text-[10px] text-[#AE9885] italic mt-3 leading-relaxed">
                      Aucun paiement n'est requis. Notre équipe vous recontacte personnellement pour finaliser votre expérience.
                    </p>
                  </div>
                </form>
              </CardContent>
            </Card>
          )}
        </motion.div>
      </section>
    </div>
  );
}
